/**
 * A boundary around the chrome, so a bad value can cost a row and not the editor.
 *
 * React unmounts the whole tree when a render throws and nothing catches it, and
 * for Jot that is the worst available outcome: the panel is a text field the user
 * reached for with a global shortcut, and a blank one is indistinguishable from
 * having lost the text. The failure this exists for is not hypothetical — a value
 * that arrived from a Jot Server and was *persisted* would otherwise throw on
 * every launch, with no way back that does not involve deleting a file by hand.
 *
 * So this is not an apology for unvalidated input; the validation happens at the
 * edges that read it. It is the structural half: whatever gets through lands on
 * the smallest subtree that can still be wrong.
 *
 * Deliberately without a retry. The state that threw is normally the state that is
 * still there, so a retry button would offer a loop, and the boundary latches for
 * the session instead. The editor above it keeps working, which is the point.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Rendered in place of `children`. Keep it the same size, or the panel jumps. */
  fallback: ReactNode
}

export class ErrorBoundary extends Component<Props, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The only report there is: no telemetry, and the message can quote whatever
    // caused it, so this stays in the console with everything else.
    console.error('jot: a component failed to render', error, info.componentStack)
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
